import { Link, useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { Seo } from "@/components/Seo";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { GraduationCap, CheckCircle2, Compass, ArrowRight, Heart, Sparkles } from "lucide-react";
import { InstallAppButton } from "@/pwa/InstallAppButton";
import { APP_VERSION } from "@/config/version";
import { trackEvent } from "@/lib/analytics";

const programs = [
  {
    title: "PMKVY",
    description: "Short-term skill courses across 100+ sectors, fully funded by the Government of India.",
  },
  {
    title: "NAPS Apprenticeships",
    description: "Earn a stipend while you learn on the job with registered Indian employers.",
  },
  {
    title: "NPTEL & SWAYAM",
    description: "Free online courses from IITs and IIMs. Audit for free; pay only if you want a certificate.",
  },
  {
    title: "Free English Resources",
    description: "Curated BBC Learning English, British Council, and Cambridge tools to boost employability.",
  },
];

const eligibility = [
  "Indian citizen, typically aged 15–35 (varies by program)",
  "Class 10 or 12 pass for most skill programs; some require a diploma or degree",
  "Aadhaar and a working mobile number for registration",
  "Open to students, recent graduates, job seekers, and career switchers",
];

const steps = [
  {
    icon: Compass,
    title: "Explore",
    description: "Take the free career quiz or browse curated programs by sector and interest.",
  },
  {
    icon: CheckCircle2,
    title: "Check eligibility",
    description: "Review clear, plain-English criteria for every program before you apply.",
  },
  {
    icon: GraduationCap,
    title: "Apply directly",
    description: "We link straight to the official government or partner portal. No middlemen, no fees.",
  },
];

const Welcome = () => {
  const navigate = useNavigate();

  useEffect(() => {
    try {
      localStorage.setItem("wh_beta_welcome_seen", "1");
    } catch {}
  }, []);

  const onContinue = () => {
    trackEvent("beta_signup", {});
    navigate("/");
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Seo
        title="Welcome to We-Help Beta | Free Programs for Indian Youth"
        description="Welcome to the We-Help Beta. Explore 100% free government and partner programs for Indian students."
        path="/welcome"
      />
      <Header />
      <main className="flex-1">
        {/* Beta welcome */}
        <section className="bg-gradient-to-b from-primary/5 to-background py-16 md:py-24">
          <div className="container max-w-4xl text-center">
            <span className="inline-flex items-center gap-1 rounded-full border border-amber-300 bg-amber-100 px-3 py-1 text-xs font-bold uppercase tracking-wide text-amber-800">
              <Sparkles className="h-3 w-3" /> {APP_VERSION}
            </span>
            <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-foreground">
              Welcome to We-Help Beta
            </h1>
            <p className="mt-6 text-lg text-muted-foreground">
              Thank you for being one of our first users. We-Help is a free, non-commercial gateway
              to curated government and partner programs for Indian youth — no registration walls,
              no fees, no spam. Your feedback shapes what we build next.
            </p>
            <div className="mt-4 inline-flex items-center gap-2 text-sm text-muted-foreground">
              <Heart className="h-4 w-4 text-primary" />
              Found a bug or an idea? Use the Feedback button on any page.
            </div>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
              <Button size="lg" variant="cta" onClick={onContinue}>
                Continue <ArrowRight />
              </Button>
              <InstallAppButton variant="outline" size="lg" hideWhenUnavailable={false} />
            </div>
            <p className="mt-6 text-xs text-muted-foreground">
              An initiative of SkillSTEM Academy LLP
            </p>
          </div>
        </section>

        {/* Free programs */}
        <section className="py-16 md:py-20">
          <div className="container max-w-5xl">
            <div className="text-center mb-10">
              <h2 className="text-3xl font-bold text-foreground">Free programs we curate</h2>
              <p className="mt-3 text-muted-foreground">
                A snapshot of the kinds of opportunities you'll find on we-help.in.
              </p>
            </div>
            <div className="grid gap-5 sm:grid-cols-2">
              {programs.map((p) => (
                <Card key={p.title} className="border-border/60">
                  <CardHeader>
                    <CardTitle className="text-xl">{p.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground">{p.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Eligibility */}
        <section className="py-16 md:py-20 bg-muted/30">
          <div className="container max-w-3xl">
            <div className="text-center mb-10">
              <h2 className="text-3xl font-bold text-foreground">Who is eligible?</h2>
              <p className="mt-3 text-muted-foreground">
                Most programs welcome a wide range of Indian learners. Specific criteria vary by program.
              </p>
            </div>
            <ul className="space-y-4">
              {eligibility.map((item) => (
                <li key={item} className="flex items-start gap-3 rounded-lg bg-background p-4 shadow-sm">
                  <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
                  <span className="text-foreground">{item}</span>
                </li>
              ))}
            </ul>
            <p className="mt-6 text-center text-sm text-muted-foreground">
              Always confirm exact eligibility on the official program page before applying.
            </p>
          </div>
        </section>

        {/* How it works */}
        <section className="py-16 md:py-20">
          <div className="container max-w-5xl">
            <div className="text-center mb-10">
              <h2 className="text-3xl font-bold text-foreground">How it works</h2>
              <p className="mt-3 text-muted-foreground">Three simple steps from curious to enrolled.</p>
            </div>
            <div className="grid gap-6 md:grid-cols-3">
              {steps.map((s, i) => (
                <Card key={s.title} className="text-center border-border/60">
                  <CardHeader>
                    <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
                      <s.icon className="h-6 w-6" />
                    </div>
                    <CardTitle className="mt-4 text-xl">
                      {i + 1}. {s.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground">{s.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
            <div className="mt-12 text-center">
              <Button asChild size="lg" variant="cta">
                <Link to="/programs">
                  Start Exploring <ArrowRight />
                </Link>
              </Button>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default Welcome;